import React from "react";
import { motion } from "framer-motion";
import { Inbox, RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Panel, Spinner, SectionTitle, Button, Tag } from "@/components/ui";

export default function Requests() {
  const { active, loading } = useBusiness();
  const [list, setList] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!active) return;
    const { data } = await api.get(`/businesses/${active.id}/requests`);
    setList(data);
  }, [active]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!active) return <Panel className="p-8 text-center text-sm" style={{ color: "var(--text-2)" }}>Pick a workspace.</Panel>;

  return (
    <div>
      <SectionTitle eyebrow={active.name} title="Requests">
        <Button variant="ghost" size="sm" onClick={load} data-testid="requests-refresh"><RefreshCw size={14} /> Refresh</Button>
      </SectionTitle>

      {!list && <Panel className="p-8 flex justify-center"><Spinner /></Panel>}

      {list && list.length === 0 && (
        <Panel className="p-10 text-center" data-testid="requests-empty">
          <Inbox size={28} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-2)" }}>No requests yet. When the agent routes a customer request to your team, it appears here and in Trello.</p>
        </Panel>
      )}

      {list && list.length > 0 && (
        <div className="space-y-px" style={{ background: "var(--border)" }} data-testid="requests-list">
          {list.map((r, i) => (
            <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="p-4" style={{ background: "var(--s1)" }} data-testid={`request-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium truncate">{r.title}</span>
                    {r.priority === "high" && <Tag tone="warning" className="flex items-center gap-1"><AlertTriangle size={11} /> high</Tag>}
                  </div>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>{r.summary}</p>
                  <div className="text-[11px] mt-1.5 mono" style={{ color: "var(--text-muted)" }}>
                    {r.customerName || "Customer"} · +{r.waId}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {r.routedToTrello
                    ? <Tag tone="success">in Trello</Tag>
                    : <Tag>logged</Tag>}
                  {r.cardUrl && <a href={r.cardUrl} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1" style={{ color: "var(--accent-2)" }} data-testid={`request-card-${r.id}`}>open <ExternalLink size={11} /></a>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
